import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  FaAngleLeft,
  FaAnglesLeft,
  FaAnglesRight,
  FaAngleRight,
} from "react-icons/fa6/index.esm";

const Pagination = ({ pageInfo, onPageChange }) => {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const [pages, setPages] = useState([]);
  const [lastPage, setLastPage] = useState(0);

  // Define pageshandler before using it in useEffect
  const pageshandler = useCallback(() => {
    const temp = [];
    // Use totalPages from pageInfo.total if present, otherwise calculate
    const totalPages =
      pageInfo?.total ||
      Math.ceil(pageInfo?.totaluser / pageInfo?.rowsPerPage) ||
      0;
    setLastPage(totalPages);

    // Fix for edge cases when totalPages is less than 5
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) {
        temp.push(i);
      }
    } 
    // Fix for when we're near the end of the pagination
    else if (Number(pageInfo.page) > totalPages - 2) {
      // Make sure we don't push negative page numbers
      const startPage = Math.max(1, totalPages - 4);
      for (let i = startPage; i <= totalPages; i++) {
        temp.push(i);
      }
    } 
    // Fix for when we're near the beginning of the pagination
    else if (Number(pageInfo.page) <= 2) {
      for (let i = 1; i <= 5; i++) {
        temp.push(i);
      }
    } 
    // Standard case - show 2 pages before and 2 pages after current page
    else {
      for (let i = Number(pageInfo.page) - 2; i <= Number(pageInfo.page) + 2; i++) {
        if (i > 0 && i <= totalPages) {
          temp.push(i);
        }
      }
    }
    setPages(temp);
  }, [pageInfo]);

  // Now use pageshandler in useEffect after it's defined
  useEffect(() => {
    if (pageInfo?.page) {
      pageshandler();
    }
  }, [pageInfo, pageshandler]);

  const navigationHandler = useCallback(
    (page) => {
      if (onPageChange) {
        // If onPageChange prop exists, use it (for client-side pagination)
        onPageChange(page);
      } else {
        // Otherwise use URL-based navigation (for server-side pagination)
        const params = new URLSearchParams(search);
        params.set("page", page);
        navigate(`${pathname}?${params.toString()}`);
      }
    },
    [pathname, search, navigate, onPageChange]
  );

  return (
    <div className="pagination">
      <button
        className={Number(pageInfo?.page) !== 1 ? "active" : ""}
        onClick={() =>
          Number(pageInfo?.page) !== 1
            ? navigationHandler(1)
            : null
        }
        disabled={Number(pageInfo?.page) === 1}
      >
        <FaAnglesLeft size={8} />
      </button>
      <button
        className={Number(pageInfo?.page) !== 1 ? "active" : ""}
        onClick={() =>
          Number(pageInfo?.page) !== 1 ? navigationHandler(Number(pageInfo.page) - 1) : null
        }
        disabled={Number(pageInfo?.page) === 1}
      >
        <FaAngleLeft size={8} />
      </button>
      <div className="pages">
        {pages?.map((el) => (
          <button
            key={`${pathname}-${el}`}
            className={
              Number(pageInfo?.page) === el ? "number active" : "active"
            }
            onClick={() =>
              Number(pageInfo?.page) !== el ? navigationHandler(el) : null
            }
          >
            <span>{el}</span>
          </button>
        ))}
      </div>
      <button
        className={Number(pageInfo?.page) !== lastPage ? "active" : ""}
        onClick={() =>
          Number(pageInfo?.page) !== lastPage
            ? navigationHandler(Number(pageInfo?.page) + 1)
            : null
        }
        disabled={Number(pageInfo?.page) === lastPage || lastPage === 0}
      >
        <FaAngleRight size={8} />
      </button>
      <button
        className={Number(pageInfo?.page) !== lastPage ? "active" : ""}
        onClick={() =>
          Number(pageInfo?.page) !== lastPage ? navigationHandler(lastPage) : null
        }
        disabled={Number(pageInfo?.page) === lastPage || lastPage === 0}
      >
        <FaAnglesRight size={8} />
      </button>
      <p>
        Showing {pageInfo?.totaluser > 0 ? (Number(pageInfo?.page) - 1) * pageInfo?.rowsPerPage + 1 : 0} to{" "}
        {pageInfo?.page * pageInfo?.rowsPerPage < pageInfo?.totaluser
          ? pageInfo?.page * pageInfo?.rowsPerPage
          : pageInfo?.totaluser}{" "}
        of {pageInfo?.totaluser}
      </p>
    </div>
  );
};

export default Pagination;
